import { useEffect, useState, useRef } from 'react';
import { supabase } from 'common/src/supabase';
import { Button, Input } from '@nextui-org/react';
import { toast } from 'react-toastify';
import { fetchUserProfile } from 'common/src/api/users';
import { getMfaStatus } from 'common/src/api/mfa';
import { Link } from 'wouter';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import useBrand from 'common/src/hooks/useBrand';

type props = {
  setView: (view: 'login' | 'register') => void;
};

const Login = ({ setView }: props) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string>();
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { assets: brandAssets } = useBrand();
  const [backgroundIndex, setBackgroundIndex] = useState(0);

  useEffect(() => {
    brandAssets.loginBackgrounds.forEach((src) => {
      const image = new Image();
      image.src = src;
    });

    if (brandAssets.loginBackgrounds.length < 2) return;

    const intervalId = window.setInterval(() => {
      setBackgroundIndex(
        (currentIndex) =>
          (currentIndex + 1) % brandAssets.loginBackgrounds.length
      );
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [brandAssets.loginBackgrounds]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Por favor ingresa email y contraseña');
      return;
    }

    if (!captchaToken) {
      toast.error('Por favor completa la verificación de seguridad');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) {
        toast.error(error.message);
        // Resetear CAPTCHA después de error
        turnstileRef.current?.reset();
        setCaptchaToken(undefined);
        return;
      }

      // Con verificación en dos pasos activa, la app pide el código antes de
      // seguir (MfaChallenge); el perfil se lee recién con la sesión validada.
      if (data.user && (await getMfaStatus()).status === 'required') {
        return;
      }

      if (data.user) {
        // Verificar si el usuario tiene exporter_id
        const userProfile = await fetchUserProfile(data.user.id);

        if (
          !userProfile?.exporter_id &&
          !userProfile?.roles?.includes('superadmin') &&
          !userProfile?.roles?.includes('document_helper')
        ) {
          // No tiene exporter_id ni rol interno permitido, hacer logout
          await supabase.auth.signOut();
          toast.error('No tienes permisos para acceder a esta plataforma');
          // Resetear CAPTCHA después de error
          turnstileRef.current?.reset();
          setCaptchaToken(undefined);
          return;
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Error al iniciar sesión');
      // Resetear CAPTCHA después de error
      turnstileRef.current?.reset();
      setCaptchaToken(undefined);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='relative flex h-screen w-screen bg-background'>
      {/* Brand Logo */}
      <div className='absolute left-1/2 top-5 -translate-x-1/2 lg:left-5 lg:translate-x-0'>
        <div className='flex items-center'>
          <img
            src={brandAssets.fullLogoLight}
            alt={brandAssets.name}
            className='h-10 w-auto max-w-[70vw] dark:hidden sm:h-12 lg:h-8'
          />
          <img
            src={brandAssets.fullLogoDark}
            alt={brandAssets.name}
            className='hidden h-10 w-auto max-w-[70vw] dark:block sm:h-12 lg:h-8'
          />
        </div>
      </div>

      {/* Login Form */}
      <div className='flex w-full items-center justify-center lg:w-1/2'>
        <div className='w-full max-w-md p-8 bg-background rounded-lg shadow-md mx-4'>
          {/* design-ignore: pantalla de login */}
          <h2 className='text-2xl font-semibold mb-6'>Iniciar Sesión</h2>
          <form onSubmit={handleLogin} className='flex flex-col gap-4'>
            <Input
              type='email'
              label='Email'
              placeholder='tu@email.com'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isRequired
            />
            <Input
              type='password'
              label='Contraseña'
              placeholder='••••••••'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
            />

            {/* Cloudflare Turnstile */}
            <div className='flex justify-center'>
              <Turnstile
                ref={turnstileRef}
                siteKey='0x4AAAAAACKyDNxQSOYs1Pwy'
                onSuccess={(token) => {
                  setCaptchaToken(token);
                }}
                onError={() => {
                  setCaptchaToken(undefined);
                  toast.error(
                    'Error en la verificación de seguridad. Intenta recargar la página.'
                  );
                }}
                onExpire={() => {
                  setCaptchaToken(undefined);
                }}
              />
            </div>

            <Button
              type='submit'
              color='primary'
              className='w-full'
              isLoading={loading}
              isDisabled={!captchaToken}
            >
              Iniciar Sesión
            </Button>
            <div className='text-center'>
              <Link href='/forgot-password'>
                <a className='text-sm text-brand-900 hover:underline'>
                  ¿Olvidaste tu contraseña?
                </a>
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right side */}
      <div className='relative hidden w-1/2 flex-col-reverse overflow-hidden rounded-medium shadow-small lg:flex'>
        {brandAssets.loginBackgrounds.map((src, index) => (
          <div
            key={src}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out ${
              backgroundIndex === index ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ backgroundImage: `url(${src})` }}
          />
        ))}
        {/* <div className='bg-gradient-to-t from-black to-transparent'>
          <div className='flex flex-col items-end gap-4 p-6 rounded-lg'>
            <div className='flex flex-col items-end'>
              <p className='text-right text-2xl font-bold text-white'>
                Cherry Market
              </p>
              <p className='text-right text-lg text-white/80'>
                Your trusted fruit marketplace
              </p>
            </div>
            <p className='w-full text-right text-2xl text-white/60'>
              <span className='font-medium'>"</span>
              <span className='font-normal italic'>
                Connecting farmers and buyers with the freshest cherries from
                around the world.
              </span>
              <span className='font-medium'>"</span>
            </p>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Login;
